import { useLocalizedPath, useSiteLocale } from "../i18n/useLocalizedPath";
import { localizeHtmlLinks } from "../i18n/localizedRoutes";
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  Button,
  HStack,
  Grid,
  GridItem,
  Tooltip,
  AspectRatio,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowBackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  MinusIcon,
} from "@chakra-ui/icons";
import { localizedNewsCover, newsData } from "../data/newsData";
import NewsSidebar from "./NewsSidebar";
import { imageMap } from "../utils/imageMap";
import { getAssetLanguageKey, normalizeSiteLanguage } from "../i18n/language";
import { getNewsCopy, getNewsPartnerCopy } from "../i18n/content";
import SEO from "./SEO";

const NewsDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const localizePath = useLocalizedPath();
  const siteLocale = useSiteLocale();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const assetLang = getAssetLanguageKey(activeLanguage);
  const locale = normalizeSiteLanguage(activeLanguage);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isFullSize, setIsFullSize] = useState(false);

  const newsItem = newsData.find((item) => item.id === id);
  const detailImages =
    newsItem?.imagesByLang?.[assetLang] || newsItem?.images || [];
  const coverImageKey =
    (newsItem && localizedNewsCover(newsItem, activeLanguage)) ||
    newsItem?.thumbnailByLang?.[assetLang] ||
    detailImages[0] ||
    newsItem?.coverImage ||
    newsItem?.thumbnail;
  const coverImage = coverImageKey
    ? imageMap[coverImageKey] || coverImageKey
    : "";

  useEffect(() => {
    setSelectedImageIndex(0);
    setIsFullscreen(false);
  }, [activeLanguage, id]);

  const getDateDisplay = (date: string) => {
    try {
      const dateObj = new Date(date);
      if (dateObj.toString() === "Invalid Date") {
        return date;
      }
      return dateObj.toLocaleDateString(
        locale === "en" ? "en-US" : locale === "zh-CN" ? "zh-CN" : "zh-HK",
      );
    } catch (error) {
      return date;
    }
  };

  if (!newsItem) {
    return (
      <Container maxW="800px" py={8}>
        <SEO title={t("news.notFound")} noindex />
        <VStack spacing={4}>
          <Text>{t("news.notFound")}</Text>
          <Button
            onClick={() => navigate(localizePath("/news"))}
            leftIcon={<ArrowBackIcon />}
          >
            {t("news.backToNews")}
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <>
      <SEO
        title={getNewsCopy(t, newsItem.id, "seoTitle") || `${getNewsCopy(t, newsItem.id, "title")} | Deep Soul`}
        description={getNewsCopy(t, newsItem.id, "seoDescription") || getNewsCopy(t, newsItem.id, "content")
          .replace(/<[^>]*>/g, "")
          .substring(0, 160)}
        keywords={`${getNewsCopy(t, newsItem.id, "title")}, ${t("seo.pages.newsDetailKeywords")}`}
        canonical={`https://deepsoulai.com/news/${id}`}
        ogImage={coverImage}
        ogImageAlt={getNewsCopy(t, newsItem.id, "coverImageAlt") || getNewsCopy(t, newsItem.id, "title")}
      />
      <Container
        maxW="1400px"
        w="100%"
        py={{ base: 4, md: 8 }}
        px={{ base: 4, md: 6 }}
        overflowX="hidden"
      >
        <Grid
          templateColumns={{ base: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr)" }}
          gap={{ base: 4, lg: 8 }}
          w="100%"
          minW={0}
        >
          <GridItem minW={0} maxW="100%">
            <Box
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              p={{ base: 4, md: 6 }}
              minW={0}
              maxW="100%"
            >
              <VStack spacing={{ base: 4, md: 6 }} align="stretch" minW={0}>
                <Image
                  src={coverImage}
                  alt={getNewsCopy(t, newsItem.id, "coverImageAlt") || getNewsCopy(t, newsItem.id, "title")}
                  w="100%"
                  h={{ base: "auto", lg: "500px" }}
                  maxH={{ base: "70vh", lg: "500px" }}
                  objectFit="contain"
                  borderRadius="lg"
                  fallbackSrc="https://via.placeholder.com/800x300?text=News+Image"
                />
                <Box>
                  <Heading
                    as="h1"
                    size={{ base: "lg", md: "xl" }}
                    overflowWrap="anywhere"
                  >
                    {getNewsCopy(t, newsItem.id, "title")}
                  </Heading>
                  <Text fontSize="sm" color="gray.500" paddingTop={2}>
                    {getNewsCopy(t, newsItem.id, "dateDisplay") ||
                      getDateDisplay(newsItem.date)}
                  </Text>
                </Box>

                {/* <Divider /> */}
                <Box minW={0} maxW="100%">
                  <Box
                    fontSize={{ base: "md", md: "lg" }}
                    lineHeight="1.8"
                    minW={0}
                    maxW="100%"
                    overflowWrap="anywhere"
                    wordBreak="break-word"
                    dangerouslySetInnerHTML={{
                      __html: localizeHtmlLinks(getNewsCopy(t, newsItem.id, "detailContent"), siteLocale),
                    }}
                    sx={{
                      "& a": {
                        color: "blue.500",
                        textDecoration: "underline",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        _hover: {
                          color: "blue.700",
                          textDecoration: "none",
                        },
                      },
                      "& ul": {
                        pl: 5,
                        my: 3,
                      },
                      "& li": {
                        mb: 2,
                      },
                      "& p": {
                        mb: 4,
                        color: "gray.700",
                        maxW: "100%",
                        overflowWrap: "anywhere",
                      },
                      "& h2": {
                        mt: 8,
                        mb: 3,
                        color: "gray.900",
                        fontSize: { base: "xl", md: "2xl" },
                        fontWeight: 700,
                        lineHeight: 1.35,
                        letterSpacing: "-0.015em",
                        overflowWrap: "anywhere",
                      },
                      "& strong": {
                        fontWeight: 600,
                      },
                      "& img, & video, & iframe": {
                        display: "block",
                        maxW: "100%",
                        height: "auto",
                      },
                      "& table": {
                        display: "block",
                        maxW: "100%",
                        overflowX: "auto",
                      },
                      "& pre": {
                        maxW: "100%",
                        overflowX: "auto",
                        whiteSpace: "pre",
                      },
                    }}
                  />

                  {newsItem.partners && newsItem.partners.length > 0 && (
                    <Box mt={8}>
                      <Text
                        fontSize="md"
                        fontWeight={600}
                        color="gray.700"
                        mb={4}
                        letterSpacing="0.02em"
                      >
                        {t("news.partners")}
                      </Text>
                      <Grid
                        templateColumns={{
                          base: "1fr",
                          sm: "repeat(3, 1fr)",
                        }}
                        gap={4}
                      >
                        {newsItem.partners.map((partner) => {
                          const partnerName = getNewsPartnerCopy(
                            t,
                            newsItem.id,
                            partner.id,
                            "name",
                          );
                          const partnerDescription = getNewsPartnerCopy(
                            t,
                            newsItem.id,
                            partner.id,
                            "description",
                          );
                          const content = (
                            <VStack spacing={3} align="center" h="100%">
                              <Box
                                w="100%"
                                h="72px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg="white"
                                borderRadius="lg"
                                px={3}
                                py={2}
                              >
                                <Image
                                  src={partner.logo}
                                  alt={partnerName}
                                  maxH="56px"
                                  maxW="100%"
                                  objectFit="contain"
                                  fallbackSrc="https://via.placeholder.com/160x56?text=Partner"
                                />
                              </Box>
                              <Text
                                fontWeight={600}
                                fontSize="sm"
                                textAlign="center"
                                color="gray.800"
                              >
                                {partnerName}
                              </Text>
                              {partnerDescription && (
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  textAlign="center"
                                  lineHeight="1.5"
                                >
                                  {partnerDescription}
                                </Text>
                              )}
                            </VStack>
                          );

                          const cardProps = {
                            borderWidth: "1px",
                            borderColor: "gray.100",
                            borderRadius: "xl",
                            bg: "gray.50",
                            px: 4,
                            py: 5,
                            h: "100%",
                            transition: "all 0.2s",
                            _hover: {
                              borderColor: "gray.200",
                              bg: "white",
                              boxShadow: "sm",
                              transform: "translateY(-2px)",
                            },
                          };

                          return partner.url ? (
                            <Box
                              as="a"
                              key={partner.id}
                              href={partner.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              textDecoration="none"
                              {...cardProps}
                            >
                              {content}
                            </Box>
                          ) : (
                            <Box key={partner.id} {...cardProps}>
                              {content}
                            </Box>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}

                  {detailImages.length > 0 && (
                    <Grid
                      templateColumns={{
                        base: "minmax(0, 1fr)",
                        sm: "repeat(auto-fit, minmax(200px, 1fr))",
                      }}
                      gap={4}
                      mt={6}
                      minW={0}
                      maxW="100%"
                    >
                      {detailImages.map((image, index) => (
                        <Image
                          key={index}
                          src={imageMap[image] || image}
                          alt={`${getNewsCopy(t, newsItem.id, "title")} - Image ${index + 1}`}
                          w="100%"
                          h={{ base: "auto", sm: "200px" }}
                          maxH={{ base: "70vh", sm: "200px" }}
                          objectFit={{ base: "contain", sm: "cover" }}
                          borderRadius="lg"
                          cursor="pointer"
                          _hover={{ opacity: 0.8 }}
                          onClick={() => {
                            setSelectedImageIndex(index);
                            setIsFullscreen(true);
                          }}
                          fallbackSrc="https://via.placeholder.com/200x200?text=Image"
                        />
                      ))}
                    </Grid>
                  )}
                  {/* youtube link */}
                  {newsItem.youtubeLink && (
                    <AspectRatio ratio={16 / 9} w="100%" maxW="760px" mt={6}>
                      <iframe
                        src={newsItem.youtubeLink}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </AspectRatio>
                  )}
                </Box>

                {/* <Divider /> */}

                <HStack justify="center">
                  <Button
                    colorScheme="primary"
                    size="sm"
                    p={5}
                    fontWeight={500}
                    onClick={() => navigate(localizePath("/news"))}
                  >
                    {t("news.viewMoreNews")}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </GridItem>
          <GridItem display={{ base: "none", lg: "block" }} minW={0}>
            <NewsSidebar />
          </GridItem>
        </Grid>

        {isFullscreen && (
          <Box
            position="fixed"
            top={0}
            left={0}
            w="100vw"
            h="100vh"
            bg="black"
            zIndex={9999}
          >
            <Button
              position="absolute"
              top={4}
              right={4}
              zIndex={2}
              borderRadius="full"
              opacity={0.7}
              _hover={{ opacity: 1 }}
              onClick={() => setIsFullscreen(false)}
            >
              ✕
            </Button>
            <Tooltip
              label={isFullSize ? t("news.fitScreen") : t("news.fullSize")}
              placement="bottom"
            >
              <Button
                position="absolute"
                top={4}
                right={16}
                zIndex={2}
                borderRadius="full"
                opacity={0.7}
                _hover={{ opacity: 1 }}
                onClick={() => setIsFullSize(!isFullSize)}
              >
                {isFullSize ? <MinusIcon /> : <AddIcon />}
              </Button>
            </Tooltip>
            {detailImages.length > 1 && (
              <>
                <Button
                  position="absolute"
                  left={4}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                  borderRadius="full"
                  opacity={0.7}
                  _hover={{ opacity: 1 }}
                  onClick={() =>
                    setSelectedImageIndex((prev) =>
                      prev > 0 ? prev - 1 : detailImages.length - 1,
                    )
                  }
                >
                  <ChevronLeftIcon />
                </Button>
                <Button
                  position="absolute"
                  right={4}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                  borderRadius="full"
                  opacity={0.7}
                  _hover={{ opacity: 1 }}
                  onClick={() =>
                    setSelectedImageIndex((prev) =>
                      prev < detailImages.length - 1 ? prev + 1 : 0,
                    )
                  }
                >
                  <ChevronRightIcon />
                </Button>
              </>
            )}
            <Box
              w="100%"
              h="100%"
              overflow="auto"
              display="flex"
              alignItems={isFullSize ? "flex-start" : "center"}
              justifyContent="center"
              p={isFullSize ? 4 : 0}
            >
              <Image
                src={
                  detailImages[selectedImageIndex]
                    ? imageMap[detailImages[selectedImageIndex]] ||
                      detailImages[selectedImageIndex]
                    : ""
                }
                alt="Fullscreen image"
                maxW={isFullSize ? "none" : "100%"}
                maxH={isFullSize ? "none" : "100%"}
                objectFit={isFullSize ? "none" : "contain"}
              />
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
};

export default NewsDetail;

